/**
 * RouteErrorBoundary — hiba-placeholder a FEHÉR KÉPERNYŐ helyett (kör-71, FE `2`).
 *
 * Mérve (2026-09-19, `origin/main`): a `kgc-web`-ben `0` error boundary volt. Egy render-hiba
 * így a TELJES React-fát lebontotta ⇒ üres, fehér oldal, menü nélkül — a pultnál ez
 * „elromlott a gép?". A boundary a tartalom-részt fogja körbe (`ProtectedLayout`), így a menü
 * és a fejléc megmarad, és a felhasználó el tud navigálni.
 *
 * A hívó `key={pathname}`-nel mountolja ⇒ navigáláskor a hiba-állapot magától feloldódik.
 * Az újrapróba TELJES újratöltés, nem újrarenderelés: a determinisztikus hiba újrarenderelve
 * azonnal visszatérne, a deploy utáni elavult chunk (`Failed to fetch dynamically imported
 * module`) viszont csak friss betöltéssel gyógyul.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { consoleCapture } from './helpdesk/lib/console-capture';
import { QueryErrorState } from './QueryErrorState';

/**
 * T-71-20 (e), 🌐 CR `#2159` MEDIUM: a reload a `consoleCapture` memóriabeli pufferét törölné,
 * így az „Újra" után a helpdesk-jegyhez már nem lenne mit csatolni. Reload ELŐTT átmentjük.
 */
export function reloadKeepingCapturedErrors(): void {
  consoleCapture.persistForReload();
  window.location.reload();
}

interface Props {
  children: ReactNode;
  /** Teszt-varrat: alapértelmezés a teljes újratöltés. */
  onRetry?: () => void;
}

interface State {
  failed: boolean;
}

export class RouteErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // A hiba NEM nyelődik el: a konzolban (és a böngésző hibagyűjtőjében) ott marad.
    console.error('[RouteErrorBoundary]', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.failed) {
      return (
        <div className="p-6">
          <QueryErrorState
            messageKey="common.renderError"
            onRetry={this.props.onRetry ?? reloadKeepingCapturedErrors}
          />
        </div>
      );
    }
    return this.props.children;
  }
}
